import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Trophy } from 'lucide-react';
import ShaderBackground from './ShaderBackground';
import { KioskLeagueBranding } from '../services/KioskService';
import { useKioskBasePath } from '../hooks/useKioskBasePath';

interface KioskShellProps {
  league: KioskLeagueBranding;
  displayName?: string;
  leagueSlug: string;
  showLeaderboardLink?: boolean;
  showSignOut?: boolean;
  onSignOut?: () => void;
  children: React.ReactNode;
  maxWidthClass?: string;
}

export const KioskShell: React.FC<KioskShellProps> = ({
  league,
  displayName,
  leagueSlug,
  showLeaderboardLink = true,
  showSignOut = false,
  onSignOut,
  children,
  maxWidthClass = 'max-w-5xl',
}) => {
  const basePath = useKioskBasePath();
  const primary = league.primary_color || '#f97316';
  const secondary = league.secondary_color || '#3b82f6';
  const accent = league.accent_color || primary;
  const sponsors = league.sponsor_logos || [];
  const sponsorLinks = league.sponsor_links || [];

  return (
    <div className="relative min-h-screen bg-slate-950 text-white">
      <div className="fixed inset-0 z-0 overflow-hidden">
        <ShaderBackground preset="aurora" primaryColor={primary} secondaryColor={secondary} />
      </div>
      <div className="fixed inset-0 z-0 bg-black/55" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="bg-black/60 backdrop-blur-sm border-b border-white/5">
          <div className={`${maxWidthClass} mx-auto px-4 sm:px-8 py-2 flex items-center justify-center gap-3`}>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">
              Powered by The Singing League
            </span>
            <img src="/TSL_Logo.png" alt="The Singing League" className="h-12 md:h-14 object-contain" />
          </div>
        </div>

        <header
          className="relative border-b border-white/10 shadow-2xl backdrop-blur-md"
          style={{
            background: `linear-gradient(135deg, ${primary}E6, ${secondary}CC)`,
          }}
        >
          <div className="absolute inset-0 bg-black/25 pointer-events-none" />
          <div className={`relative ${maxWidthClass} mx-auto px-4 sm:px-8 py-6 flex items-center justify-between gap-3 flex-wrap`}>
            <Link to={displayName ? `/${basePath}/${leagueSlug}/me` : `/${basePath}/${leagueSlug}`} className="flex items-center gap-4 group">
              {league.logo_url ? (
                <img
                  src={league.logo_url}
                  alt={league.name}
                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-contain bg-white/95 p-1.5 shadow-lg border border-white/20"
                />
              ) : (
                <div
                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl flex items-center justify-center font-black text-2xl shadow-lg border border-white/20"
                  style={{ backgroundColor: accent }}
                >
                  {(league.name || 'K').charAt(0)}
                </div>
              )}
              <div>
                <div className="text-2xl sm:text-3xl font-black tracking-tight leading-tight drop-shadow-lg">
                  {league.name}
                </div>
                {displayName && (
                  <div className="text-sm text-white/85 font-medium drop-shadow">
                    Welcome, {displayName}
                  </div>
                )}
              </div>
            </Link>

            <div className="flex items-center gap-2">
              {showLeaderboardLink && (
                <Link
                  to={`/${basePath}/${leagueSlug}/leaderboard`}
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl bg-white/15 hover:bg-white/25 border border-white/25 backdrop-blur-sm transition shadow-lg"
                >
                  <Trophy className="w-4 h-4" />
                  <span>Leaderboard</span>
                </Link>
              )}
              {showSignOut && onSignOut && (
                <button
                  onClick={onSignOut}
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm transition"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              )}
            </div>
          </div>
        </header>

        <main className={`flex-1 w-full ${maxWidthClass} mx-auto px-4 sm:px-8 py-8 sm:py-12`}>
          {children}
        </main>

        <footer className="px-4 sm:px-8 py-6 border-t border-white/10 bg-black/40 backdrop-blur-md">
          <div className="max-w-5xl mx-auto">
            {sponsors.length > 0 && (
              <div className="flex items-center justify-between gap-6">
                {sponsors.map((logo, i) => {
                  const link = sponsorLinks[i];
                  const img = (
                    <img
                      src={logo}
                      alt={`Sponsor ${i + 1}`}
                      className="h-16 sm:h-20 md:h-24 object-contain opacity-90 hover:opacity-100 transition"
                    />
                  );
                  return link ? (
                    <a key={i} href={link} target="_blank" rel="noreferrer" className="flex-1 flex justify-center">
                      {img}
                    </a>
                  ) : (
                    <div key={i} className="flex-1 flex justify-center">{img}</div>
                  );
                })}
              </div>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default KioskShell;
